'use client';

import { useEffect, useRef, useMemo } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { analytics } from '@/services/analytics';

/**
 * Derive page category from pathname for analytics categorization
 */
function derivePageCategory(pathname: string): string | undefined {
  if (pathname.startsWith('/partner/dashboard')) return 'Partner Portal';
  if (pathname.startsWith('/partner')) return 'Partner Auth';
  if (
    pathname.startsWith('/volunteer/my-events') ||
    pathname.startsWith('/volunteer/account')
  ) {
    return 'Volunteer Portal';
  }
  if (pathname.startsWith('/volunteer')) return 'Volunteer Auth';
  if (pathname.startsWith('/events')) return 'Events';
  if (pathname.startsWith('/organization')) return 'Organization';
  if (pathname.startsWith('/kindness-challenge')) return 'Challenge';
  if (pathname === '/' || pathname === '/landing') return 'Marketing';
  return undefined;
}

/**
 * AnalyticsPageTracker - Automatic page view tracking for all routes
 *
 * This component tracks page views automatically on route changes.
 * It captures:
 * - Current path
 * - Search parameters (including UTM params)
 * - Referrer
 * - Page title (after DOM updates)
 * - Page category (derived from route pattern)
 *
 * User Identity:
 * - If a user is identified (via analytics.identifyUser()), their page views
 *   will be associated with their user ID for product analytics
 * - If no user is identified, page views will be tracked with anonymousId only
 * - User context persists across navigations until explicitly cleared
 *
 * Place this component in the root layout to track all page views
 * across the entire application without explicit tracking calls in
 * each page component.
 */
export default function AnalyticsPageTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Memoize search string to stabilize dependency
  const searchString = useMemo(() => searchParams.toString(), [searchParams]);

  const previousPathRef = useRef<string | null>(null);
  const lastTrackedTime = useRef<number>(0);

  useEffect(() => {
    // Build full path with search params
    const fullPath = searchString ? `${pathname}?${searchString}` : pathname;

    // Skip if same path (prevents duplicate tracking)
    if (previousPathRef.current === fullPath) {
      return;
    }

    // Prevent rapid-fire duplicates (debounce within 500ms)
    const now = Date.now();
    if (now - lastTrackedTime.current < 500) {
      return;
    }

    previousPathRef.current = fullPath;
    lastTrackedTime.current = now;

    let cancelled = false;

    // Initialize analytics and track page view
    analytics
      .initialize()
      .then(() => {
        if (cancelled) return;

        // Extract UTM parameters and other query params
        const properties: Record<string, string> = {};

        // Add all search params as properties
        searchParams.forEach((value, key) => {
          properties[key] = value;
        });

        // Get page title after a brief delay to allow DOM updates
        setTimeout(() => {
          if (cancelled) return;

          const title = document.title || pathname;

          // Track the page view using the generic pageView method
          analytics.pageView(title, {
            path: pathname,
            url: fullPath,
            title,
            category: derivePageCategory(pathname),
            referrer: document.referrer,
            ...properties
          });
        }, 100);
      })
      .catch((error) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error(
          `[AnalyticsPageTracker] Analytics initialization failed:`,
          error
        );
      });

    return () => {
      cancelled = true;
    };
  }, [pathname, searchString, searchParams]);

  return null;
}
